// Helpers for mapping between API comparators/values and UI representations.
// Regex comparators use composite UI keys (e.g. regex_startsWith) split at the API boundary.
import { endOfDay, startOfDay } from "date-fns";

import type { ComparatorOption, DateSubType, IEmployeeField, QueryCondition } from "./types";
import { DATE_TYPES, NUMERIC_TYPES, TEXT_TYPES } from "./types";

import Cookies from "../../utils/Cookies";

import {
	ANNIVERSARY_COMPARATORS,
	DEFAULT_COMPARATORS,
	IS_MANAGER_FIELD_ID,
	IS_MANAGER_FIELD_NAME,
	NUMERIC_COMPARATORS,
	RELATIVE_DATE_COMPARATORS,
	STATIC_DATE_COMPARATORS,
	TEXT_COMPARATORS,
} from "./constants";

export const getFieldType = (field: QueryCondition["field"], employeeFields: IEmployeeField[]): string => {
	if (field === IS_MANAGER_FIELD_ID) return "boolean";
	if (typeof field === "object" && "oid" in field) {
		const found = employeeFields.find((ef) => ef._id === field.oid);
		return found?.type ?? "";
	}
	return "";
};

export const getFieldName = (field: QueryCondition["field"], employeeFields: IEmployeeField[]): string => {
	if (field === IS_MANAGER_FIELD_ID) return IS_MANAGER_FIELD_NAME;
	if (typeof field === "object" && "oid" in field) {
		const found = employeeFields.find((ef) => ef._id === field.oid);
		return found?.name ?? "";
	}
	return "";
};

export const getFieldId = (field: QueryCondition["field"]): string => {
	if (field === IS_MANAGER_FIELD_ID) return IS_MANAGER_FIELD_ID;
	if (typeof field === "object" && "oid" in field) return field.oid;
	return "";
};

export const getFieldOptions = (field: QueryCondition["field"], employeeFields: IEmployeeField[]): string[] => {
	if (typeof field === "object" && "oid" in field) {
		const found = employeeFields.find((ef) => ef._id === field.oid);
		return found?.options ?? [];
	}
	return [];
};

export const getComparatorsForFieldType = (fieldType: string, dateSubType?: DateSubType): ComparatorOption[] => {
	if (NUMERIC_TYPES.includes(fieldType)) return NUMERIC_COMPARATORS;
	if (TEXT_TYPES.includes(fieldType)) return TEXT_COMPARATORS;
	if (DATE_TYPES.includes(fieldType)) {
		if (dateSubType === "static") return STATIC_DATE_COMPARATORS;
		if (dateSubType === "anniversary") return ANNIVERSARY_COMPARATORS;
		return RELATIVE_DATE_COMPARATORS;
	}
	return DEFAULT_COMPARATORS;
};

export const toUIComparator = (apiComparator: string, apiValue: QueryCondition["value"]): string => {
	if (apiComparator === "regex" && typeof apiValue === "string") {
		if (apiValue.startsWith("^")) return "regex_startsWith";
		if (apiValue.endsWith("$")) return "regex_endsWith";
		return "regex_contains";
	}
	if (apiComparator === "not_regex" && typeof apiValue === "string") {
		if (apiValue.startsWith("^")) return "not_regex_notStartsWith";
		if (apiValue.endsWith("$")) return "not_regex_notEndsWith";
		return "not_regex_notContains";
	}
	return apiComparator;
};

export const toAPIComparator = (uiComparator: string): string => {
	if (uiComparator.startsWith("regex_")) return "regex";
	if (uiComparator.startsWith("not_regex_")) return "not_regex";
	return uiComparator;
};

export const wrapRegexValue = (uiComparator: string, rawValue: string): string => {
	if (uiComparator === "regex_startsWith" || uiComparator === "not_regex_notStartsWith") return `^${rawValue}`;
	if (uiComparator === "regex_endsWith" || uiComparator === "not_regex_notEndsWith") return `${rawValue}$`;
	return rawValue;
};

export const unwrapRegexValue = (apiValue: string): string => {
	if (apiValue.startsWith("^")) return apiValue.slice(1);
	if (apiValue.endsWith("$")) return apiValue.slice(0, -1);
	return apiValue;
};

export const detectDateSubType = (value: QueryCondition["value"]): DateSubType => {
	if (value && typeof value === "object") {
		if ("date" in value) return "static";
		if ("anniversary_date" in value) return "anniversary";
		if ("relative_date" in value) return "relative";
	}
	return "static";
};

export const adjustDateToDayBoundary = (date: Date, comparator: string): string => {
	let dateValue = new Date(date);
	dateValue.setHours(dateValue.getHours() + dateValue.getTimezoneOffset() / 60);
	if (comparator === "gt") {
		dateValue = endOfDay(dateValue);
	} else {
		dateValue = startOfDay(dateValue);
	}
	dateValue.setHours(dateValue.getHours() - dateValue.getTimezoneOffset() / 60);
	return dateValue.toISOString();
};

export const getTimezone = (): string => {
	return Cookies.get("_tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
};

export const getComparatorLabel = (apiComparator: string, apiValue: QueryCondition["value"]): string => {
	const uiKey = toUIComparator(apiComparator, apiValue);
	const allComparators = [
		...DEFAULT_COMPARATORS,
		...TEXT_COMPARATORS,
		...NUMERIC_COMPARATORS,
		...STATIC_DATE_COMPARATORS,
		...RELATIVE_DATE_COMPARATORS,
		...ANNIVERSARY_COMPARATORS,
	];
	return allComparators.find((c) => c.value === uiKey)?.label ?? apiComparator;
};

export const getDisplayValue = (
	condition: QueryCondition,
	employeeFields: IEmployeeField[],
	displayNames?: Record<string, string>
): string => {
	const { value, comparator } = condition;
	if (value === null || value === undefined) return "";
	if (typeof value === "boolean") return value ? "True" : "False";
	if (typeof value === "number") return String(value);

	if (typeof value === "string") {
		if (comparator === "regex" || comparator === "not_regex") {
			return unwrapRegexValue(value);
		}
		return value;
	}

	if (typeof value === "object") {
		if ("date" in value) {
			const date = new Date(value.date);
			date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
			return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
		}
		if ("relative_date" in value) {
			const length = Math.abs(value.relative_date);
			const unit = value.relative_date_unit;
			const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
			return `${length} ${length === 1 ? unitLabel.slice(0, -1) : unitLabel} Ago`;
		}
		if ("anniversary_date" in value) {
			if (comparator === "eq_anniversary" && value.anniversary_date === 0) return "";
			const length = Math.abs(value.anniversary_date);
			const unit = value.anniversary_date_unit;
			const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
			return `${length} ${length === 1 ? unitLabel.slice(0, -1) : unitLabel}`;
		}
		if ("oid" in value) {
			return displayNames?.[value.oid] ?? value.oid;
		}
	}

	return String(value);
};
